/**
 * Apple Pay / Google Pay buttons via Stripe's Express Checkout Element.
 *
 * Framework-agnostic on purpose: the same helper is used from Vue SFCs and from
 * the course checkout, which is inline JS in a Blade template.
 *
 * Why this replaces `elements.create('paymentRequestButton')`: the Payment
 * Request Button is Stripe's legacy wallet API, and every call site here created
 * one without a `paymentmethod` listener — the sheet opened and the payment
 * never completed. The Express Checkout Element confirms through the very same
 * `stripe.confirmPayment({ elements })` call the card form already uses, so
 * wallets and cards share one code path and one PaymentIntent.
 *
 * Wallets are deliberately the only methods enabled. The backend pins
 * `payment_method_types: ['card']` and confirms payments synchronously
 * (PaymentService::isPaymentIntentSucceeded requires a succeeded charge right
 * away), so redirect-based methods would report failure on an otherwise fine
 * payment. Apple Pay and Google Pay are card-family and settle inline.
 */

const WALLET_ONLY_PAYMENT_METHODS = {
  link: 'never',
  paypal: 'never',
  amazonPay: 'never',
  klarna: 'never',
  // 'always' rather than the 'auto' default, which means "show where Stripe
  // judges it good for conversion" — not "show wherever it works". Two whole
  // surfaces sit behind this flag: Apple Pay on non-Safari desktop browsers
  // (Apple's iOS 18 QR hand-off, where the buyer scans a code and pays on their
  // iPhone) and Google Pay in Safari. Both are unreachable on 'auto'.
  //
  // The trade-off Stripe documents: the button also renders for people with no
  // wallet set up, who then get a sign-in / QR screen they may not be able to
  // finish. The card form stays mounted alongside, so that is a wasted click,
  // never a dead end.
  applePay: 'always',
  googlePay: 'always',
}

const noop = () => {}

/**
 * @param {object}   options
 * @param {object}   options.stripe        Stripe instance (loadStripe result or global Stripe()).
 * @param {object}   options.elements      Elements group already created with the PaymentIntent clientSecret.
 * @param {string}   options.selector      CSS selector of the mount target.
 * @param {string}   options.clientSecret  PaymentIntent client secret.
 * @param {string}   options.returnUrl     return_url for wallets that need a redirect (3DS).
 * @param {Function} [options.onConfirm]   async () => {} — server-side confirmation after success.
 * @param {Function} [options.onError]     (message) => {}
 * @param {Function} [options.onAvailable] (bool) => {} — no wallet is available until this reports true.
 * @param {Function} [options.onBusy]      (bool) => {} — for disabling the surrounding form while paying.
 * @param {number}   [options.buttonHeight]
 * @returns {{ destroy: Function, element: object }|null}
 */
export function mountExpressCheckout({
  stripe,
  elements,
  selector,
  clientSecret,
  returnUrl,
  onConfirm = noop,
  onError = noop,
  onAvailable = noop,
  onBusy = noop,
  buttonHeight = 48,
}) {
  if (!stripe || !elements || !clientSecret || !document.querySelector(selector)) {
    onAvailable(false)

    return null
  }

  let element

  try {
    element = elements.create('expressCheckout', {
      paymentMethods: WALLET_ONLY_PAYMENT_METHODS,
      buttonHeight,
      layout: { maxColumns: 2, maxRows: 1 },
    })
  } catch (e) {
    // An older Stripe.js or an unsupported Elements mode: fail closed and keep
    // the card form as the only visible option.
    onAvailable(false)
    // eslint-disable-next-line no-console
    console.warn('Express Checkout Element unavailable:', e)

    return null
  }

  // Nothing is guaranteed to render: no wallet on the device, an unregistered
  // domain, or a currency the wallet does not support. Stripe reports this
  // through `availablepaymentmethodschange`, so the container stays hidden
  // until it says otherwise.
  element.on('availablepaymentmethodschange', ({ paymentMethods }) => {
    onAvailable(Boolean(paymentMethods))
  })

  element.on('loaderror', (event) => {
    onAvailable(false)
    onError(event?.error?.message || '')
  })

  element.on('confirm', async () => {
    onBusy(true)

    try {
      // No `payment_method_data`: the wallet supplies name, email and billing
      // address itself, and overriding them conflicts with the sheet's data.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      })

      if (error) {
        onError(error.message || 'Payment failed. Please try again.')

        return
      }

      if (paymentIntent) {
        await onConfirm(paymentIntent)
      }
    } catch (e) {
      onError(e?.message || 'Payment failed. Please try again.')
    } finally {
      onBusy(false)
    }
  })

  element.on('cancel', () => {
    onBusy(false)
  })

  element.mount(selector)

  return {
    element,
    destroy() {
      try {
        // destroy(), not unmount(): the Elements group is rebuilt whenever the
        // payment method changes, and an unmounted-but-live element leaks and
        // blocks re-creation of the same element type.
        element.destroy()
      } catch (e) {
        /* already gone */
      }
    },
  }
}

export default mountExpressCheckout
